import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withFormik, Field } from 'formik';
import * as Yup from 'yup';
import { FormGroup, Label, FormFeedback, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import Flatpickr from 'react-flatpickr';
import { connect } from 'react-redux';


import { renderHelpText, renderField } from 'core/utils/formik';
import PropertyLookup from 'components/PropertyLookup';
import UserLookup from 'components/UserLookup';
import DeviceFields from './DeviceFields';
import { fetchDeviceSchema } from '../../../store/test-form-reducer';

const jsDateFormat = 'm/d/Y h:i K';
const momentDateFormat = 'MM/DD/YYYY hh:mm A';

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
};

const resultMask = createNumberMask({
  allowDecimal: true,
  decimalLimit: 1,
  prefix: '',
});

const calculateDuration = (placedAt, retrievedAt) => {
  if (!placedAt && !retrievedAt) { return; }
  return moment(retrievedAt).diff(moment(placedAt), 'hours');
};

const setDuration = (duration, form) => {
  form.setFieldValue('duration', duration);
  form.setFieldTouched('duration', true);
};

class AppFields extends Component {
  handleLookupChange = (field, form, resource) => {
    resource = resource || {};
    form.setFieldValue(field.name, resource.id);
  };

  handlePropertyLookupChange = (field, form, property) => {
    form.setFieldValue(field.name, (property || {}).id);
    if (property.stateId) {
      this.props.fetchDeviceSchema(property.stateId);
      form.setFieldValue('state_id', property.stateId);
    }
  };

  handlePlacedAtChange = (field, form, dateString) => {
    const { values: { retrieved_at } } = form;
    const { name } = field;
    const placedAt = moment(dateString).toISOString();

    form.setFieldValue(name, placedAt);
    setDuration(calculateDuration(placedAt, retrieved_at), form);
  }

  handleRetrievedAtChange = (field, form, dateString) => {
    const { values: { placed_at } } = form;
    const { name } = field;
    const retrievedAt = moment(dateString).toISOString();

    form.setFieldValue(name, retrievedAt);
    setDuration(calculateDuration(placed_at, retrievedAt), form);
  }

  renderPropertyLookup = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { property } } = form;

    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="property_id">Select Property</Label>
        <PropertyLookup
          onChange={propertyValue => this.handlePropertyLookupChange(field, form, propertyValue)}
          onBlur={() => form.setFieldTouched(name, true)}
          property={property}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
        {renderHelpText(
          <span>
            or&nbsp;
            <Link to="/clients/new">add client</Link>
          </span>
        )}
      </FormGroup>
    )
  };


  renderPlacedByLookup = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { placed_by } } = form;
    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="placed_by">Placed By</Label>
        <UserLookup
          onChange={device => this.handleLookupChange(field, form, device)}
          onBlur={() => form.setFieldTouched(field.name, true)}
          userId={(placed_by || {}).id}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
        {renderHelpText(
          <span>
            or&nbsp;
            <Link to="/account/users/new">add user</Link>
          </span>
        )}
      </FormGroup>
    )
  };

  renderPlacedAt = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { placed_at } } = form;
    const value = moment(placed_at);

    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="placed_at">Placed At</Label>
        <Flatpickr
          className="form-control"
          options={{
            enableTime: true,
            onChange: array => this.handlePlacedAtChange(field, form, array[0]),
            dateFormat: jsDateFormat,
            defaultDate: value.format(momentDateFormat),
            minuteIncrement: 1,
          }}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
        {renderHelpText("Saved in account's default timezone.")}
      </FormGroup>
    )
  };

  renderRetrievedByLookup = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { retrieved_by } } = form;
    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="retrieved_by">Retrieved By</Label>
        <UserLookup
          onChange={user => this.handleLookupChange(field, form, user)}
          onBlur={() => form.setFieldTouched(field.name, true)}
          userId={(retrieved_by || {}).id}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
      </FormGroup>
    )
  };

  renderRetrievedAt = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { retrieved_at } } = form;
    const value = moment(retrieved_at);
    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="retrieved_at">Retrieved At</Label>

        <Flatpickr
          className="form-control"
          options={{
            enableTime: true,
            onChange: array => this.handleRetrievedAtChange(field, form, array[0]),
            dateFormat: jsDateFormat,
            defaultDate: value.format(momentDateFormat),
            minuteIncrement: 1,
          }}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
        {renderHelpText("Saved in account's default timezone.")}
      </FormGroup>
    )
  };

  renderDuration = ({ field, form }) => {
    const { name } = field;
    const { errors, touched, values: { duration } } = form;

    if (!duration) {
      return null;
    }

    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="duration">Duration</Label>
        <p className="form-control-static">{duration} hours</p>
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
      </FormGroup>
    );
  };

  renderResult = ({ field, form }) => {
    const { name } = field;
    const { errors, touched } = form;

    return (
      <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
        <Label for="result">Result</Label>
        <MaskedInput
          className="form-control"
          mask={resultMask}
          {...field}
        />
        {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
      </FormGroup>
    );
  };

  renderEditFields = () => {
    const { values } = this.props;

    if (!values.id) {
      return null;
    }

    return (
      <div>
        <Field
          name="retrieved_by_id"
          component={this.renderRetrievedByLookup}
        />

        <Field
          name="retrieved_at"
          component={this.renderRetrievedAt}
        />

        <Field
          name="duration"
          component={this.renderDuration}
        />

        <Field
          type="textarea"
          name="public_comments"
          label="Public Comments"
          helpText="include any observed tampering or deviation from required test conditions, will be displayed on consumer report"
          component={renderField}
        />
      </div>
    );
  };

  render() {
    const {
      handleSubmit,
      isSubmitting,
      isValid,
      values,
    } = this.props;

    return (
      <form onSubmit={handleSubmit}>
        <Field
          name="property_id"
          component={this.renderPropertyLookup}
        />

        <DeviceFields
          testDevices={values.test_devices}
          editing={!!values.id}
        />

        <Field
          name="placed_by_id"
          component={this.renderPlacedByLookup}
        />

        <Field
          name="placed_at"
          component={this.renderPlacedAt}
        />

        {this.renderEditFields()}

        <Field
          type="textarea"
          name="internal_notes"
          label="Internal Notes"
          component={renderField}
        />

        <Button
          type="submit"
          color="primary"
          disabled={!isValid || isSubmitting}
        >
          Save
        </Button>
      </form>
    )
  }
};

AppFields.propTypes = propTypes;

const setValidationSchema = id => {
  const setCrmValidation = () => {
    let schema = Yup.object({
      location: Yup.string().required('Required'),
      location_other: Yup.mixed().notRequired(),
      device: Yup.object({
        id: Yup.string().required('Required'),
      })
    });

    if (id) {
      schema = schema.shape({
        result: Yup.string().required('Required'),
      });
    }

    return schema;
  };

  const setPassiveValidation = () => {
    let schema = Yup.object({
      location: Yup.string().required('Required'),
      location_other: Yup.mixed().notRequired(),
      device: Yup.object({
        manufacturer: Yup.string().required('Required'),
        deviceable: Yup.object({
          serial_number: Yup.string().required('Required'),
          device_type: Yup.string().required('Required'),
        }),
      })
    });

    if (id) {
      schema = schema.shape({
        result: Yup.string().required('Required'),
      });
    }

    return schema;
  };

  const devices = Yup.lazy(({ device_type }) => {
    switch (device_type) {
      case 'crm':
        return setCrmValidation();
      case 'passive':
        return setPassiveValidation();
      default:
        throw new Error(`unknown device type ${device_type}`);
    }
  });

  const schema = {
    property_id: Yup.string().required('Required'),
    placed_by_id: Yup.string().required('Required'),
    placed_at: Yup.string().required('Required'),
    test_devices: Yup.array().min(1).of(devices),
  }

  if (id) {
    schema.retrieved_by_id = Yup.string().required('Required');
    schema.retrieved_at = Yup.string().required('Required');
  }

  return Yup.object(schema);
}

AppFields = withFormik({
  mapPropsToValues: ({ initialValues }) => {
    const { id, property, placed_by, placed_at, retrieved_by, retrieved_at, duration, public_comments, internal_notes } = initialValues;

    if (id) {
      initialValues = Object.assign(initialValues, {
        property_id: property.id,
        placed_by_id: placed_by.id,
      });
      if (retrieved_by) { initialValues.retrieved_by_id = retrieved_by.id; }
      if (!retrieved_at) { initialValues.retrieved_at = moment().toISOString(); }
      if (!duration) { initialValues.duration = calculateDuration(placed_at, initialValues.retrieved_at); }
      if (!public_comments) { initialValues.public_comments = ''; }
      if (!internal_notes) { initialValues.internal_notes = ''; }
    }

    return initialValues;
  },
  validationSchema: ({ initialValues }) => {
    const { id } = initialValues;
    return setValidationSchema(id);
  },
  handleSubmit: (values, formikBag) => {
    formikBag.setSubmitting(false);
    formikBag.props.onSubmit(values);
  },
  displayName: 'AppFields',
  isInitialValid: ({ initialValues }) => {
    const { id } = initialValues;
    const schema = setValidationSchema(id);
    const result = schema.isValidSync(initialValues);
    return result;
  }
})(AppFields);

const mapDispatchToProps = dispatch => ({
  fetchDeviceSchema: (id) => dispatch(fetchDeviceSchema(id)),
});

export default connect(null, mapDispatchToProps)(AppFields);



// WEBPACK FOOTER //
// ./src/features/test/TestForm/AppFields.js